import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Search, Calendar, User, Eye, ArrowRight, ClipboardList,
  Bookmark, Share2, MessageCircle, ChevronRight, Filter,
  FileText, Heart, Star, BookOpen, GraduationCap
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import HeroSection from '../components/HeroSection';

const NewsletterList = () => {
  const [newsletters, setNewsletters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const navigate = useNavigate();
  const categories = [
    { value: '', label: 'All', icon: ClipboardList },
    { value: 'announcement', label: 'Announcements', icon: Star },
    { value: 'event', label: 'Events', icon: Calendar },
    { value: 'achievement', label: 'Achievements', icon: Star },
    { value: 'department', label: 'Department', icon: BookOpen },
    { value: 'general', label: 'General', icon: FileText }
  ];

  useEffect(() => {
    fetchNewsletters();
  }, [selectedCategory]);

  const fetchNewsletters = async () => {
    try {
      let url = `${process.env.REACT_APP_BASE_URL}/api/newsletter/published`;
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      setNewsletters(data);
    } catch (error) {
      console.error('Error fetching newsletters:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    { value: newsletters.length, label: 'Total Newsletters' },
    { value: newsletters.filter(n => n.featured).length, label: 'Featured Stories' },
    { 
      value: newsletters.reduce((acc, curr) => acc + curr.viewCount, 0), 
      label: 'Total Views' 
    }
  ];

  const renderNewsletterCard = (newsletter) => (
    <div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -8, scale: 1.02 }}
      className="group relative bg-white rounded-2xl shadow-sm hover:shadow-xl 
        transition-all duration-300 overflow-hidden cursor-pointer"
      onClick={() => navigate(`/newsletter/${newsletter._id}`)}
    >
      {/* Background Pattern - Lower z-index */}
      <div className="absolute inset-0 opacity-[0.03] group-hover:opacity-[0.05] z-0">
        <svg className="w-full h-full">
          <defs>
            <pattern id={`grid-${newsletter._id}`} x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill={`url(#grid-${newsletter._id})`} />
        </svg>
      </div>
  
      {/* Cover Image Section */}
      <div className="relative h-48 overflow-hidden z-10">
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent" />
        <img
          src={newsletter.coverImage || '/api/placeholder/400/300'}
          alt={newsletter.title}
          className="w-full h-full object-cover transform group-hover:scale-110 transition-all duration-700"
        />
        
        {/* Category Badge */}
        <div className="absolute top-4 left-4">
          <span className="px-3 py-1.5 rounded-full text-sm font-medium 
            bg-white/90 text-purple-600 shadow-lg backdrop-blur-sm">
            {newsletter.category}
          </span>
        </div>
      </div>
  
      {/* Content Section */}
      <div className="relative p-6 z-10">
        <h2 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">
          {newsletter.title}
        </h2>
        {newsletter.subtitle && (
          <p className="text-gray-600 mb-4 line-clamp-2">{newsletter.subtitle}</p>
        )}
  
        {/* Metadata */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <User size={16} className="text-gray-400 mr-2" />
              <span className="text-sm text-gray-600">{newsletter.author.name}</span>
            </div>
            <div className="flex items-center">
              <Calendar size={16} className="text-gray-400 mr-2" />
              <span className="text-sm text-gray-600">
                {format(new Date(newsletter.createdAt), 'MMM dd, yyyy')}
              </span>
            </div>
          </div>
          
          <div className="flex items-center">
            <Eye size={16} className="text-gray-400 mr-2" />
            <span className="text-sm text-gray-600">{newsletter.viewCount} views</span>
          </div>
        </div>
  
        {/* Read More Button */}
        <div className="relative z-20">
          <div className="flex items-center justify-end">
            <button
              className="inline-flex items-center px-4 py-2 rounded-lg bg-purple-600 
                text-white hover:bg-purple-700 transition-colors group-hover:scale-105"
            >
              Read More
              <ChevronRight size={16} className="ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <HeroSection
        title="Stay Updated with Our Community"
        subtitle="Discover the latest news, achievements, and updates from our vibrant alumni network"
        showSearch={true}
        searchConfig={{
          value: searchTerm,
          onChange: (e) => setSearchTerm(e.target.value),
          placeholder: "Search newsletters..."
        }}
        statsConfig={stats}
        gradientColors={{
          from: "purple-800",
          via: "indigo-800",
          to: "blue-900"
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 mt-10 relative z-10">
        {/* Category Filters */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-2">
            {categories.map(category => {
              const Icon = category.icon;
              return (
                <motion.button
                  key={category.value}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedCategory(category.value)}
                  className={`inline-flex items-center px-4 py-2 rounded-lg 
                    shadow-sm transition-colors ${
                      selectedCategory === category.value
                        ? 'bg-purple-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <Icon size={16} className="mr-2" />
                  {category.label}
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Newsletter Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full"
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {newsletters
              .filter(newsletter =>
                newsletter.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                newsletter.subtitle?.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map(newsletter => renderNewsletterCard(newsletter))
            }
          </div>
        )}

        {newsletters.length === 0 && !loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <FileText size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-xl text-gray-600">No newsletters found</p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default NewsletterList;